import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaVideo,
  FaClipboardCheck,
  FaExternalLinkAlt,
  FaClock,
  FaBookOpen
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getAssetUrl } from '../../../utils/apiBase';
import { generateModulesUrl } from '../../../utils/urlSecurity';
import {
  WEEKDAY_LABELS,
  EVENT_TYPE_META,
  buildMonthGrid,
  formatMonthYear,
  formatEventTime,
  formatEventDate,
  getEventMeta,
  getEventsForDay,
  getMonthRange,
  isSameDay,
  parseCalendarDate,
  toDateKey
} from '../../utils/calendarHelpers';

const LEGEND_ITEMS = [
  EVENT_TYPE_META.vilt,
  EVENT_TYPE_META.assessment
];

const NAV_BUTTON_CLASS =
  'border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2 px-3';

const EventCalendarView = ({
  title = 'Event Calendar',
  description = 'VILT sessions, live meetings, and assessment due dates in one place.',
  emptyMessage = 'Your VILT sessions, meetings, and assessment due dates will appear here.',
  loadEvents,
  vltPath = '/user/vlt',
  assessmentMode = 'user',
  legendItems = LEGEND_ITEMS
}) => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const monthCells = useMemo(
    () => buildMonthGrid(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth]
  );

  const today = useMemo(() => new Date(), []);

  const monthEventCount = useMemo(() => {
    const { start, end } = getMonthRange(currentMonth.getFullYear(), currentMonth.getMonth());
    const startKey = toDateKey(start);
    const endKey = toDateKey(end);

    return events.filter((event) => {
      if (event.allDay) {
        const dayKey = toDateKey(event.start);
        return dayKey >= startKey && dayKey <= endKey;
      }
      const eventStart = parseCalendarDate(event.start);
      const eventEnd = parseCalendarDate(event.end || event.start);
      if (!eventStart) return false;
      return (eventEnd || eventStart) >= start && eventStart <= end;
    }).length;
  }, [events, currentMonth]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { start, end } = getMonthRange(currentMonth.getFullYear(), currentMonth.getMonth());
      const data = await loadEvents({
        start: start.toISOString(),
        end: end.toISOString()
      });
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading calendar events:', error);
      toast.error('Failed to load calendar events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [currentMonth]);

  const handlePreviousMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  const handleOpenEvent = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedEvent(null);
  };

  const handleJoinMeeting = (event) => {
    if (!event?.url) {
      toast.info('No meeting link available for this event.');
      return;
    }
    const targetUrl = String(event.url).startsWith('http') ? event.url : getAssetUrl(event.url);
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleAssessmentAction = (event) => {
    if (!event?.sourceId) return;

    if (assessmentMode === 'user') {
      if (event.isLocked || event.resultStatus) {
        navigate(`/user/assessments/${event.sourceId}/result`);
      } else {
        navigate(`/user/assessments/${event.sourceId}`);
      }
    } else if (assessmentMode === 'admin') {
      navigate(`/admin/assessments/${event.sourceId}/assignments`);
    } else if (assessmentMode === 'org') {
      navigate(`/organization/assessments/${event.sourceId}/analytics`);
    } else if (assessmentMode === 'instructor') {
      navigate(`/instructor/assessments/${event.sourceId}/analytics`);
    }

    handleCloseModal();
  };

  const handleViewVlt = () => {
    navigate(vltPath);
    handleCloseModal();
  };

  const handleCourseAction = (event) => {
    if (!event?.sourceId) {
      return;
    }

    navigate(generateModulesUrl(event.sourceId, 'user'));
    handleCloseModal();
  };

  const selectedMeta = selectedEvent ? getEventMeta(selectedEvent) : null;
  const showAssessmentAction = assessmentMode !== 'none' && selectedEvent?.type === 'assessment';
  const showCourseAction = assessmentMode === 'user' && selectedEvent?.type === 'course';
  const assessmentActionLabel = assessmentMode === 'admin' || assessmentMode === 'org'
    ? 'View Assessment'
    : assessmentMode === 'instructor'
      ? 'View Analytics'
      : (selectedEvent?.isLocked ? 'View Result' : 'Start Assessment');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4" />
          <p className="text-gray-600 text-lg font-medium">Loading calendar events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
              <FaCalendarAlt />
            </div>
            <div>
              <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">{title}</h2>
              <p className="text-gray-600 text-sm sm:text-base m-0">{description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6 mb-6">
        <Row className="g-3 align-items-center">
          <Col md={6} sm={12}>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{formatMonthYear(currentMonth)}</h3>
            <p className="text-gray-600 text-sm mb-0">
              {monthEventCount} event{monthEventCount === 1 ? '' : 's'} this month
            </p>
          </Col>
          <Col md={6} sm={12}>
            <div className="flex flex-wrap justify-content-md-end gap-2">
              <Button bsPrefix="custom-hamburger" className={NAV_BUTTON_CLASS} onClick={handlePreviousMonth} aria-label="Previous month">
                <FaChevronLeft />
                Previous
              </Button>
              <Button bsPrefix="custom-hamburger" className={NAV_BUTTON_CLASS} onClick={handleCurrentMonth}>
                Current Month
              </Button>
              <Button bsPrefix="custom-hamburger" className={NAV_BUTTON_CLASS} onClick={handleNextMonth} aria-label="Next month">
                Next
                <FaChevronRight />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-5 mb-6">
        <p className="text-xs uppercase text-gray-500 font-semibold mb-3">Event types</p>
        <div className="flex flex-wrap gap-2">
          {legendItems.map((item) => (
            <span
              key={item.label}
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${item.badgeClass}`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${item.dotClass}`} />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {monthEventCount === 0 ? (
          <div className="text-center py-8 sm:py-10 mb-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaCalendarAlt className="text-white text-3xl" />
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No Events This Month</h5>
            <p className="text-gray-600 text-base sm:text-lg mb-0">{emptyMessage}</p>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              {WEEKDAY_LABELS.map((label) => (
                <div key={label} className="px-2 py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 border border-gray-200 border-t-0 rounded-b-xl overflow-hidden">
              {monthCells.map((day, index) => {
                const dayEvents = getEventsForDay(events, day);
                const isToday = isSameDay(day, today);
                const isCurrentMonth = day && day.getMonth() === currentMonth.getMonth();

                return (
                  <div
                    key={`${toDateKey(day)}-${index}`}
                    className={`min-h-[110px] sm:min-h-[130px] border-r border-b border-gray-200 p-2 ${
                      !day ? 'bg-gray-50/80' : 'bg-white'
                    } ${isToday ? 'bg-purple-50/60' : ''}`}
                  >
                    {day && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold ${
                              isToday
                                ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-sm'
                                : isCurrentMonth
                                  ? 'text-gray-800'
                                  : 'text-gray-400'
                            }`}
                          >
                            {day.getDate()}
                          </span>
                        </div>

                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((event) => {
                            const meta = getEventMeta(event);
                            return (
                              <button
                                key={event.id}
                                type="button"
                                onClick={() => handleOpenEvent(event)}
                                className={`w-full text-left px-2 py-1 rounded-md text-[11px] sm:text-xs font-medium truncate border ${meta.chipClass}`}
                                title={event.title}
                                aria-label={`Open event ${event.title}`}
                              >
                                {!event.allDay && (
                                  <span className="opacity-90 mr-1">
                                    {(parseCalendarDate(event.start) || new Date(event.start)).toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                                {event.title}
                              </button>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <button
                              type="button"
                              onClick={() => handleOpenEvent(dayEvents[3])}
                              className="text-[11px] text-purple-600 font-semibold px-1"
                            >
                              +{dayEvents.length - 3} more
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showDetailModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header
          closeButton
          closeVariant="white"
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4"
        >
          <Modal.Title className="text-base sm:text-lg">Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 sm:p-6 bg-gray-50">
          {selectedEvent && selectedMeta && (
            <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${selectedMeta.badgeClass}`}>
                    {selectedMeta.label}
                  </span>
                  <h4 className="text-xl font-bold text-gray-900 mt-3 mb-1">{selectedEvent.title}</h4>
                  {selectedEvent.description && (
                    <p className="text-gray-600 text-sm mb-0">{selectedEvent.description}</p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                  {selectedEvent.type === 'assessment'
                    ? <FaClipboardCheck size={20} />
                    : selectedEvent.type === 'course'
                      ? <FaBookOpen size={20} />
                      : <FaVideo size={20} />}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl bg-purple-50 p-3">
                  <p className="text-xs uppercase text-purple-500 mb-1">
                    {selectedEvent.type === 'assessment' ? 'Assessment Date' : 'Date'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mb-0 flex items-center gap-2">
                    <FaCalendarAlt className="text-purple-600" />
                    {formatEventDate(selectedEvent.availableFrom || selectedEvent.start)}
                  </p>
                </div>
                <div className="rounded-xl bg-indigo-50 p-3">
                  <p className="text-xs uppercase text-indigo-500 mb-1">
                    {selectedEvent.type === 'assessment' ? 'Assessment Time' : 'Time'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mb-0 flex items-center gap-2">
                    <FaClock className="text-indigo-600" />
                    {selectedEvent.type === 'assessment' && !selectedEvent.allDay
                      ? (parseCalendarDate(selectedEvent.availableFrom || selectedEvent.start)
                        || new Date(selectedEvent.start)
                      ).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                      : formatEventTime(selectedEvent)}
                  </p>
                </div>
              </div>

              {(selectedEvent.type === 'assessment' || selectedEvent.type === 'course') && selectedEvent.availableTo && (
                <div className="rounded-xl bg-lime-50 p-3 mb-4">
                  <p className="text-xs uppercase text-lime-600 mb-1">
                    {selectedEvent.type === 'course' ? 'Scheduled Until' : 'Available Until'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mb-0">
                    {formatEventDate(selectedEvent.availableTo)}
                    {' · '}
                    {(parseCalendarDate(selectedEvent.availableTo) || new Date(selectedEvent.availableTo))
                      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              )}

              {selectedEvent.courseName && (
                <div className="rounded-xl bg-gray-50 p-3 mb-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">Course</p>
                  <p className="text-sm font-semibold text-gray-900 mb-0">{selectedEvent.courseName}</p>
                </div>
              )}

              {selectedEvent.organizationName && (
                <div className="rounded-xl bg-gray-50 p-3 mb-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">Organization</p>
                  <p className="text-sm font-semibold text-gray-900 mb-0">{selectedEvent.organizationName}</p>
                </div>
              )}

              {selectedEvent.instructorName && (
                <div className="rounded-xl bg-gray-50 p-3 mb-4">
                  <p className="text-xs uppercase text-gray-500 mb-1">Instructor</p>
                  <p className="text-sm font-semibold text-gray-900 mb-0">{selectedEvent.instructorName}</p>
                </div>
              )}

              {selectedEvent.type === 'assessment' && assessmentMode === 'user' && (
                <div className="rounded-xl bg-lime-50 p-3 mb-4">
                  <p className="text-xs uppercase text-lime-600 mb-1">Assessment Status</p>
                  <p className="text-sm font-semibold text-gray-900 mb-0">
                    {selectedEvent.isLocked
                      ? 'Passed'
                      : selectedEvent.resultStatus
                        ? `Last result: ${selectedEvent.resultStatus}`
                        : 'Not attempted yet'}
                  </p>
                </div>
              )}

              {selectedEvent.type === 'assessment' && assessmentMode !== 'user' && selectedEvent.assignmentType && (
                <div className="rounded-xl bg-lime-50 p-3 mb-4">
                  <p className="text-xs uppercase text-lime-600 mb-1">Assignment Type</p>
                  <p className="text-sm font-semibold text-gray-900 mb-0 text-capitalize">
                    {String(selectedEvent.assignmentType).replace(/_/g, ' ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-gray-50 p-4">
          <div className="w-full flex flex-wrap gap-2 justify-end">
            <Button bsPrefix="custom-hamburger" className={NAV_BUTTON_CLASS} onClick={handleCloseModal}>
              Close
            </Button>

            {showAssessmentAction && (
              <Button
                bsPrefix="custom-hamburger"
                className="border-1 border-lime-600 bg-lime-600 text-white rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center gap-2 px-4 py-2"
                onClick={() => handleAssessmentAction(selectedEvent)}
              >
                <FaClipboardCheck />
                {assessmentActionLabel}
              </Button>
            )}

            {showCourseAction && (
              <Button
                bsPrefix="custom-hamburger"
                className="border-0 text-white rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center gap-2 px-4 py-2"
                style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
                onClick={() => handleCourseAction(selectedEvent)}
              >
                <FaBookOpen />
                Open Course
              </Button>
            )}

            {(selectedEvent?.type === 'meeting' || selectedEvent?.type === 'vilt') && selectedEvent?.url && (
              <Button
                bsPrefix="custom-hamburger"
                className="border-0 text-white rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center gap-2 px-4 py-2"
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
                onClick={() => handleJoinMeeting(selectedEvent)}
              >
                <FaExternalLinkAlt />
                Join Meeting
              </Button>
            )}

            {(selectedEvent?.type === 'meeting' || selectedEvent?.type === 'vilt') && (
              <Button bsPrefix="custom-hamburger" className={NAV_BUTTON_CLASS} onClick={handleViewVlt}>
                Open VLT
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventCalendarView;
